import { css } from 'aphrodite';
import * as React from 'react';
import { useState } from 'react';
import { useMyCategories, useMyCompletions, useMyScenarios, useMySkillAreas } from '../../../queries/Scenarios';
import type { Scenario, ScenarioCompletion, ScenarioWithCompletion } from '../../types/scenarios';
import { ScoreDisplay } from '../ScoreDisplay';
import { styleSheet } from './styles';

export interface ScenarioSelectorProps {
	selectedScenarioId: string | null;
	onSelectScenario: (scenarioId: string | null) => void;
}

/**
 * Helper function to enrich scenarios with completion data
 * Combines scenario data with user's completion history to show progress
 */
const enrichScenariosWithCompletions = (
	scenarios: Scenario[],
	completions: ScenarioCompletion[]
): ScenarioWithCompletion[] => {
	return scenarios.map(scenario => {
		const scenarioCompletions = completions
			.filter(c => c.scenarioId === scenario.id)
			.sort((a, b) => new Date(b.creationDate).getTime() - new Date(a.creationDate).getTime());
		const completed = scenarioCompletions.filter(c => c.status === 'Completed');

		return {
			...scenario,
			lastCompletion: scenarioCompletions[0],
			attemptCount: scenarioCompletions.length,
			bestScore: completed.length > 0 ? Math.max(...completed.map(c => c.score ?? 0)) : undefined,
		};
	});
};

/**
 * ScenarioSelector component allows users to select a training scenario
 * before starting a practice call. Scenarios are grouped by skill area
 * and show completion statistics.
 */
export const ScenarioSelector: React.FC<ScenarioSelectorProps> = ({ selectedScenarioId, onSelectScenario }) => {
	// Track which categories and skill areas are expanded
	const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
	const [expandedSkillAreas, setExpandedSkillAreas] = useState<Set<string>>(new Set());

	// Fetch data - using my- endpoints for role-based filtering
	const { data: scenarios, isLoading: scenariosLoading } = useMyScenarios();
	const { data: completions, isLoading: completionsLoading } = useMyCompletions();
	const { data: skillAreas, isLoading: skillAreasLoading } = useMySkillAreas();
	const { data: categories, isLoading: categoriesLoading } = useMyCategories();

	// Handle loading state
	const isLoading = scenariosLoading || completionsLoading || skillAreasLoading || categoriesLoading;
	if (isLoading) {
		return (
			<div className={css(styleSheet.container)}>
				<h3 className={css(styleSheet.header)}>Select a Scenario to Practice</h3>
				<div className={css(styleSheet.loadingState)}>Loading scenarios...</div>
			</div>
		);
	}

	// Ensure data is in array format (handles various API response formats)
	const scenariosArray = Array.isArray(scenarios) ? scenarios : [];
	const completionsArray = Array.isArray(completions) ? completions : [];
	const skillAreasArray = Array.isArray(skillAreas) ? skillAreas : [];
	const categoriesArray = Array.isArray(categories) ? categories : [];

	// Handle empty state
	if (scenariosArray.length === 0) {
		return (
			<div className={css(styleSheet.container)}>
				<h3 className={css(styleSheet.header)}>Select a Scenario to Practice</h3>
				<div className={css(styleSheet.emptyState)}>
					No scenarios available. Contact your administrator to set up training scenarios.
				</div>
			</div>
		);
	}

	// Enrich scenarios with completion data
	const enrichedScenarios = enrichScenariosWithCompletions(scenariosArray, completionsArray);

	// Group scenarios by skillArea, then skillAreas by category
	const scenariosBySkillArea = new Map<string, ScenarioWithCompletion[]>();
	enrichedScenarios.forEach(scenario => {
		const existing = scenariosBySkillArea.get(scenario.skillAreaId) || [];
		scenariosBySkillArea.set(scenario.skillAreaId, [...existing, scenario]);
	});

	// Build category -> skillArea -> scenarios structure
	const categoriesWithData = categoriesArray
		.map(category => {
			const categorySkillAreas = skillAreasArray
				.filter(sa => sa.categoryId === category.id)
				.map(skillArea => ({
					skillArea,
					scenarios: scenariosBySkillArea.get(skillArea.id) || [],
				}))
				.filter(sa => sa.scenarios.length > 0);

			return {
				category,
				skillAreas: categorySkillAreas,
			};
		})
		.filter(cat => cat.skillAreas.length > 0);

	// Toggle category expansion
	const toggleCategory = (categoryId: string) => {
		const newExpanded = new Set(expandedCategories);
		if (newExpanded.has(categoryId)) {
			newExpanded.delete(categoryId);
		} else {
			newExpanded.add(categoryId);
		}
		setExpandedCategories(newExpanded);
	};

	// Toggle skill area expansion
	const toggleSkillArea = (skillAreaId: string) => {
		const newExpanded = new Set(expandedSkillAreas);
		if (newExpanded.has(skillAreaId)) {
			newExpanded.delete(skillAreaId);
		} else {
			newExpanded.add(skillAreaId);
		}
		setExpandedSkillAreas(newExpanded);
	};

	// Render a single scenario item
	const renderScenarioItem = (scenario: ScenarioWithCompletion) => {
		const isSelected = selectedScenarioId === scenario.id;
		const hasAttempts = (scenario.attemptCount ?? 0) > 0;
		const lastScore = scenario.lastCompletion?.status === 'Completed' ? scenario.lastCompletion.score : null;

		return (
			<div
				key={scenario.id}
				className={css(styleSheet.scenarioItem, isSelected && styleSheet.scenarioItemSelected)}
				onClick={() => onSelectScenario(scenario.id)}
			>
				<div className={css(styleSheet.scenarioContent)}>
					<div className={css(styleSheet.scenarioName)}>{scenario.name}</div>
					<div className={css(styleSheet.scenarioDescription)} title={scenario.description}>
						{scenario.description}
					</div>
				</div>
				<div className={css(styleSheet.scenarioStats)}>
					{hasAttempts ? (
						<>
							{lastScore !== null && lastScore !== undefined && (
								<div className={css(styleSheet.scoreContainer)}>
									<span className={css(styleSheet.scoreLabel)}>Last:</span>
									<ScoreDisplay score={lastScore} size='small' />
								</div>
							)}
							<span className={css(styleSheet.attemptCount)}>({scenario.attemptCount}x)</span>
						</>
					) : (
						<span className={css(styleSheet.notAttempted)}>Not attempted</span>
					)}
				</div>
			</div>
		);
	};

	// Render category sections with nested skill areas
	const renderCategories = () => {
		return categoriesWithData.map(({ category, skillAreas: catSkillAreas }) => {
			const isCategoryExpanded = expandedCategories.has(category.id);

			return (
				<div key={category.id} className={css(styleSheet.categorySection)}>
					<div
						className={css(styleSheet.categoryHeader, isCategoryExpanded && styleSheet.categoryHeaderExpanded)}
						onClick={() => toggleCategory(category.id)}
					>
						<h4 className={css(styleSheet.categoryTitle)}>{category.name}</h4>
						<svg
							className={css(styleSheet.chevron, isCategoryExpanded && styleSheet.chevronExpanded)}
							viewBox='0 0 24 24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							strokeLinecap='round'
							strokeLinejoin='round'
						>
							<polyline points='9 18 15 12 9 6' />
						</svg>
					</div>
					{isCategoryExpanded && (
						<div className={css(styleSheet.categoryContent)}>
							{catSkillAreas.map(({ skillArea, scenarios: areaScenarios }) => {
								const isSkillAreaExpanded = expandedSkillAreas.has(skillArea.id);

								return (
									<div key={skillArea.id} className={css(styleSheet.skillAreaSection)}>
										<div
											className={css(
												styleSheet.skillAreaHeader,
												isSkillAreaExpanded && styleSheet.skillAreaHeaderExpanded
											)}
											onClick={() => toggleSkillArea(skillArea.id)}
										>
											<h5 className={css(styleSheet.skillAreaTitle)}>{skillArea.name}</h5>
											<svg
												className={css(styleSheet.chevron, isSkillAreaExpanded && styleSheet.chevronExpanded)}
												viewBox='0 0 24 24'
												fill='none'
												stroke='currentColor'
												strokeWidth='2'
												strokeLinecap='round'
												strokeLinejoin='round'
											>
												<polyline points='9 18 15 12 9 6' />
											</svg>
										</div>
										{isSkillAreaExpanded && (
											<div className={css(styleSheet.scenarioList)}>{areaScenarios.map(renderScenarioItem)}</div>
										)}
									</div>
								);
							})}
						</div>
					)}
				</div>
			);
		});
	};

	return (
		<div className={css(styleSheet.container)}>
			<h3 className={css(styleSheet.header)}>Select a Scenario to Practice</h3>
			{renderCategories()}
		</div>
	);
};
